import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit2, Loader2, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { AdminLayout } from "@/components/admin/admin-layout";
import { Button } from "@/components/button";
import { usePageTitle } from "@/hooks/use-page-title";

interface Category {
  id: string;
  name: string;
  singular_name: string | null;
  slug: string;
  type: string;
  created_at: string;
  post_count?: number;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function CategoriesPage() {
  usePageTitle("Categories");

  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newSingularName, setNewSingularName] = useState("");
  const [newType, setNewType] = useState<string>("all");
  const [editName, setEditName] = useState("");
  const [editSingularName, setEditSingularName] = useState("");

  // Fetch categories
  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ["admin-categories-all"],
    queryFn: async () => {
      const res = await authenticatedFetch("/api/admin/categories");
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json() as Promise<{ data: Category[] }>;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch("/api/admin/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newName,
          singular_name: newSingularName || null,
          type: newType,
        }),
      });
      if (!res.ok) {
        const error = (await res.json()) as { message?: string };
        throw new Error(error.message || "Failed to create category");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories-all"] });
      setNewName("");
      setNewSingularName("");
      setNewType("all");
      setShowCreate(false);
      toast.success("Category created");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to create category";
      toast.error(message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      name,
      singular_name,
    }: {
      id: string;
      name: string;
      singular_name: string;
    }) => {
      const res = await authenticatedFetch(`/api/admin/categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, singular_name: singular_name || null }),
      });
      if (!res.ok) throw new Error("Failed to update category");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories-all"] });
      setEditingId(null);
      setEditName("");
      setEditSingularName("");
      toast.success("Category updated");
    },
    onError: () => {
      toast.error("Failed to update category");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authenticatedFetch(`/api/admin/categories/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete category");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      queryClient.invalidateQueries({ queryKey: ["admin-categories-all"] });
      toast.success("Category deleted");
    },
    onError: () => {
      toast.error("Failed to delete category");
    },
  });

  const categories = categoriesData?.data || [];

  const blogCategories = categories.filter((c) => c.type === "blog" || c.type === "all");
  const caseStudyCategories = categories.filter((c) => c.type === "case_study" || c.type === "all");

  const renderCategoryRow = (category: Category) => (
    <div
      key={category.id}
      className="flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
    >
      {editingId === category.id ? (
        <div className="flex items-center gap-2 flex-1">
          <div className="flex-1 grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Plural Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="e.g., Launches"
                className="w-full text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Singular Name</label>
              <input
                type="text"
                value={editSingularName}
                onChange={(e) => setEditSingularName(e.target.value)}
                placeholder="e.g., Launch"
                className="w-full text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          <Button
            size="sm"
            onClick={() =>
              updateMutation.mutate({
                id: category.id,
                name: editName,
                singular_name: editSingularName,
              })
            }
            disabled={updateMutation.isPending}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditingId(null);
              setEditName("");
              setEditSingularName("");
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <div>
            <span className="text-sm font-medium text-foreground">{category.name}</span>
            {category.singular_name && (
              <span className="text-xs text-muted-foreground ml-2">
                (singular: {category.singular_name})
              </span>
            )}
            <span className="text-xs text-muted-foreground ml-2">/{category.slug}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{category.post_count || 0} posts</span>
            <button
              onClick={() => {
                setEditingId(category.id);
                setEditName(category.name);
                setEditSingularName(category.singular_name || "");
              }}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                if (confirm("Are you sure you want to delete this category?")) {
                  deleteMutation.mutate(category.id);
                }
              }}
              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-accent rounded transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-foreground">Categories</h1>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Category
          </Button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium text-foreground">New Category</h2>
              <button
                onClick={() => setShowCreate(false)}
                className="p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Plural Name
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g., Launches"
                    className="w-full text-sm border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Singular Name
                  </label>
                  <input
                    type="text"
                    value={newSingularName}
                    onChange={(e) => setNewSingularName(e.target.value)}
                    placeholder="e.g., Launch"
                    className="w-full text-sm border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  className="text-sm border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Content</option>
                  <option value="blog">Blog Posts Only</option>
                  <option value="case_study">Case Studies Only</option>
                </select>
                <Button
                  onClick={() => createMutation.mutate()}
                  disabled={!newName.trim() || createMutation.isPending}
                >
                  {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Slug will be: /{slugify(newName) || "category-name"}
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : categories.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <p className="text-muted-foreground">No categories yet. Create your first one!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Blog categories */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-secondary/50">
                <h2 className="text-sm font-medium text-foreground">Blog Post Categories</h2>
              </div>
              {blogCategories.length === 0 ? (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                  No blog categories yet
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {blogCategories.map(renderCategoryRow)}
                </div>
              )}
            </div>

            {/* Case study categories */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-secondary/50">
                <h2 className="text-sm font-medium text-foreground">Case Study Categories</h2>
              </div>
              {caseStudyCategories.length === 0 ? (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                  No case study categories yet
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {caseStudyCategories.map(renderCategoryRow)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
